import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import {
  TypographyH2,
  TypographyP2Bold,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { useAtom, useSetAtom } from 'jotai';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  DEFAULT_TRIAL_EXTENSION_HOURS,
  TRIAL_EXTENSION_HOURS,
} from '../../../contracts/trial';
import {
  endTrialModalOpenAtom,
  extendTrialModalOpenAtom,
} from '../../../data/atoms/trialAtoms';
import type { ExtendFreeTrialModalHost } from './extend-free-trial.host';

export function ExtendFreeTrialModal({ host }: { host: ExtendFreeTrialModalHost }) {
  const [isAtomOpen, setIsOpen] = useAtom(extendTrialModalOpenAtom);
  const isOpen = !host.isAppleCompliantBuild() && isAtomOpen;
  const setEndTrialModalOpen = useSetAtom(endTrialModalOpenAtom);
  const intl = useIntl();

  const hoursLabel = (count: number) =>
    intl.formatMessage(
      {
        defaultMessage: '{count, plural, one {# hour} other {# hours}}',
        id: 'taAVnRuLiz',
        description: 'Trial extension duration option',
      },
      { count }
    );

  const [selectedHours, setSelectedHours] = useState<string>(
    DEFAULT_TRIAL_EXTENSION_HOURS
  );
  const { mutate, isPending } = host;

  const handleClose = () => {
    host.onExtendTrialModalBackClicked({
      selectedHours: Number(selectedHours),
    });
    setIsOpen(false);
    setEndTrialModalOpen(true);
  };

  const handleSuccess = () => {
    setIsOpen(false);
    setEndTrialModalOpen(false);
  };

  const handleExtendTrial = () => {
    host.onExtendTrialModalExtendClicked({
      selectedHours: Number(selectedHours),
    });
    mutate(Number(selectedHours), {
      onSuccess: handleSuccess,
    });
  };

  return (
    <DialogDrawer
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      <DialogDrawerContent className="flex flex-col gap-4 p-6">
        <Button
          size="lg"
          variant="link"
          className="absolute start-0 top-0 flex items-center gap-2 p-2 text-text-secondary"
          onClick={handleClose}
        >
          <ArrowLeft width={14} height={14} className="rtl:-scale-x-100" />
          <TypographyP2Bold>
            <FormattedMessage
              defaultMessage="Back"
              id="ksGj13DwjK"
              description="Button returning to the previous modal"
            />
          </TypographyP2Bold>
        </Button>

        <div className="flex flex-col items-center gap-4">
          <DialogDrawerTitle className="hidden">
            <FormattedMessage
              defaultMessage="Need more time?"
              id="V//Gyb7Rza"
              description="Heading of the modal offering to extend a free trial"
            />
          </DialogDrawerTitle>
          <DialogDrawerDescription className="hidden">
            <FormattedMessage
              defaultMessage="Extend trial form"
              id="FA+jwUFRcI"
              description="Hidden accessibility description of the extend-trial modal"
            />
          </DialogDrawerDescription>

          <FramedIcon state="pro" />
          <div className="flex flex-col items-center">
            <TypographyH2>
              <FormattedMessage
                defaultMessage="Need more time?"
                id="V//Gyb7Rza"
                description="Heading of the modal offering to extend a free trial"
              />
            </TypographyH2>
            <TypographyPBold className="text-center text-text-secondary">
              <FormattedMessage
                defaultMessage="We get it, life is busy. Have a little more on us."
                id="0aAa3GwICX"
                description="Subtitle of the modal offering to extend a free trial"
              />
            </TypographyPBold>
          </div>

          {!host.isAppleCompliantBuild() && (
            <>
              <div className="flex w-full flex-col gap-y-2">
                <TypographyP2Bold>
                  <FormattedMessage
                    defaultMessage="Extend for"
                    id="ew1ixL/srJ"
                    description="Label above the trial extension duration picker"
                  />
                </TypographyP2Bold>
                <Select
                  value={selectedHours}
                  onValueChange={setSelectedHours}
                  defaultValue={DEFAULT_TRIAL_EXTENSION_HOURS}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Select duration',
                        id: 'ne2zOn3g3s',
                        description: 'Placeholder of the trial extension duration picker',
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.ONE_HOUR}>
                      {hoursLabel(1)}
                    </SelectItem>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.TWO_HOURS}>
                      {hoursLabel(2)}
                    </SelectItem>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.FOUR_HOURS}>
                      {hoursLabel(4)}
                    </SelectItem>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.SIX_HOURS}>
                      {hoursLabel(6)}
                    </SelectItem>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.TWELVE_HOURS}>
                      {hoursLabel(12)}
                    </SelectItem>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.TWENTY_FOUR_HOURS}>
                      {hoursLabel(24)}
                    </SelectItem>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.FORTY_EIGHT_HOURS}>
                      {hoursLabel(48)}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="lg"
                className="w-full"
                onClick={handleExtendTrial}
                disabled={isPending}
              >
                {isPending ? (
                  <FormattedMessage
                    defaultMessage="Extending"
                    id="mKKqA+DF05"
                    description="Progress label while the trial extension is applied"
                  />
                ) : (
                  <FormattedMessage
                    defaultMessage="Extend my access"
                    id="0wgQfya6ui"
                    description="Button that extends the free trial"
                  />
                )}
              </Button>
            </>
          )}
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
